import React, { useMemo, useState, memo } from 'react';
import { Card, CardBody, Button, Badge } from 'reactstrap';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { ItemsSectionModal } from './ItemsSectionModal';
import { ItemsSectionTable } from './ItemsSectionTable';

import { RetiroEPPSelectorModal } from '../workflows/epp/RetiroEPPSelectorModal';
import { useEPPItems } from './epp/useEPPItems';

import { ResiduosSelectorModal } from './residuos/ResiduosSelectorModal';
import { BeachCleaningModal } from './residuos/BeachCleaningModal';
import WarehouseResidueStockPickerModal from './residuos/WarehouseResidueStockPickerModal';
import WarehouseGuideDetailPickerModal from './residuos/WarehouseGuideDetailPickerModal';
import { useResiduosItems } from './residuos/useResiduosItems';
import { applyStockSendDescriptionToLastItem } from '../utils/warehouseSendDataTransformer';
import { useFormDictionary } from './FormDictionaryContext';

const ItemsSectionSpecInternal = {
	getInitialValues: () => ({
		items: [],
	}),
	getValidationSchema: () => ({
		items: Yup.array().when('guide_category', {
			is: 'residuos',
			then: schema => schema.min(1, 'Debe agregar al menos un residuo'),
			otherwise: schema => schema.notRequired(),
		}),
	}),
	toPayload: values => {
		const rawItems = Array.isArray(values.items) ? values.items : [];
		const detalle = rawItems
			.filter(item => !item._module_vars?._is_auto_item)
			.map((item, index) => ({
				NroLinDet: index + 1,
				NmbItem: item.NmbItem || '',
				DscItem: item.DscItem || '',
				QtyItem: Number(item.QtyItem) || 0,
				UnmdItem: item.UnmdItem || '',
				MontoItem: Number(item.MontoItem).toFixed(0) || 0,
				TpoCodigo: item.TpoCodigo || '',
				VlrCodigo: item.VlrCodigo || '',
				PrcItem: Number(item.PrcItem).toFixed(0) || 0,
			}));

		return {
			dtefacele: {
				Detalle: detalle,
			},
		};
	},
};

const ItemsSectionComponent = () => {
	const validation = useFormikContext();
	const { findWarehouseById } = useFormDictionary();

	// --- Estado genérico (modal manual de ítems DTE) ---
	const [isOpen, setIsOpen] = useState(false);
	const [formItem, setFormItem] = useState({
		NmbItem: '',
		DscItem: '',
		QtyItem: '',
		UnmdItem: '',
		MontoItem: '',
		VlrCodigo: '',
		PrcItem: '',
		TpoCodigo: '',
		_readOnlyKeys: [],
		_module_vars: {},
	});

	const toggle = () => setIsOpen(prev => !prev);

	const items = useMemo(
		() => validation.values.items || [],
		[validation.values.items],
	);

	// --- Hooks de módulos ---
	const epp = useEPPItems(validation);
	const residuos = useResiduosItems(validation, items);

	// --- Handlers genéricos ---

	const resetFormItem = () => {
		setFormItem({
			NmbItem: '',
			DscItem: '',
			QtyItem: '',
			UnmdItem: '',
			MontoItem: '',
			VlrCodigo: '',
			PrcItem: '',
			TpoCodigo: '',
			_readOnlyKeys: [],
			_module_vars: {},
		});
	};

	const handleChange = e => {
		const { name, value } = e.target;
		setFormItem(prev => ({ ...prev, [name]: value }));
	};

	const handleAddItem = (keepOpen = true) => {
		const name = (formItem.NmbItem || '').trim();
		const description = (formItem.DscItem || '').trim();
		const qty = Number(formItem.QtyItem);
		const unit = (formItem.UnmdItem || '').trim();
		const tpoCode = (formItem.TpoCodigo || '').trim();
		const code = (formItem.VlrCodigo || '').trim();
		const price = Number(formItem.PrcItem);
		const amount = Number(formItem.MontoItem);

		if (!name) {
			toast.error('Debe ingresar un nombre de ítem');
			return;
		}
		if (!Number.isFinite(qty) || qty <= 0) {
			toast.error('La cantidad debe ser un número mayor a 0');
			return;
		}
		if (!unit) {
			toast.error('Debe ingresar una unidad');
			return;
		}
		if (!Number.isFinite(amount) || amount < 0) {
			toast.error('El monto debe ser un número mayor o igual a 0');
			return;
		}

		const qtyMax = Number(formItem._module_vars?.epp_qty_max) || null;
		if (qtyMax != null && isFinite(qtyMax) && qty > qtyMax) {
			toast.error(
				`La cantidad no puede superar el stock disponible (${qtyMax}).`,
			);
			return;
		}

		const next = [
			...items,
			{
				NmbItem: name,
				QtyItem: qty,
				UnmdItem: unit,
				DscItem: description,
				MontoItem: amount,
				VlrCodigo: code,
				PrcItem: price,
				TpoCodigo: tpoCode,
				_module_vars: formItem._module_vars || {},
			},
		];
		validation.setFieldValue('items', next, false);

		if (keepOpen) {
			resetFormItem();
		} else {
			toggle();
			resetFormItem();
		}
	};

	const handleAddMultiple = newItems => {
		const next = [...items, ...newItems];
		validation.setFieldValue('items', next, false);
		toggle();
		resetFormItem();
	};

	const handleRemove = index => {
		validation.setFormikState(prevState => {
			const currentItems = Array.isArray(prevState.values.items)
				? prevState.values.items
				: [];
			const wh = findWarehouseById(
				prevState.values.warehouse_origin_id,
			);
			const next = applyStockSendDescriptionToLastItem(
				currentItems.filter((_, i) => i !== index),
				{ warehouseName: wh?.name || '' },
			);
			return {
				...prevState,
				values: { ...prevState.values, items: next },
			};
		});
	};

	const handleSetItem = (key, value) => {
		setFormItem(prev => ({ ...prev, [key]: value }));
	};

	// --- Render ---

	return (
		<Card className='border h-100 mb-0'>
			<CardBody className='p-4'>
				<div className='d-flex justify-content-between align-items-center pb-2'>
					<div className='d-flex align-items-center gap-2'>
						<h5 className='m-0'>
							{residuos.isResiduos ? 'Residuos' : 'Detalle de ítems'}
						</h5>
						{epp.isRetiroEPP && items.length > 0 && (
							<Badge color='success' pill>
								{items.length} item(s) de EPP
							</Badge>
						)}
						{residuos.isResiduos && residuos.residueItemsCount > 0 && (
							<Badge color='success' pill>
								{residuos.residueItemsCount} residuo(s)
							</Badge>
						)}
					</div>

					{epp.isRetiroEPP ? (
						<Button
							color='primary'
							onClick={() => epp.setIsRetiroModalOpen(true)}
						>
							<i className='ri-checkbox-multiple-line me-1'></i>
							Seleccionar EPPs para Retiro
						</Button>
					) : residuos.isLimpiezaPlaya ? (
						<Button
							color='info'
							outline
							onClick={() => residuos.setIsBeachCleaningModalOpen(true)}
							disabled={!validation.values.residue_type_id}
						>
							<i className='ri-treasure-map-line me-1'></i>
							Agregar Limpiezas de Playa
						</Button>
					) : residuos.isEnvioBodegaPeligrosos ? (
						<Button
							color='primary'
							onClick={() => residuos.openWarehouseStockPicker()}
							disabled={
								!validation.values.residue_type_id ||
								!validation.values.establishment_id ||
								!validation.values.warehouse_origin_id
							}
						>
							<i className='ri-database-2-line me-1'></i>
							Agregar desde stock RESPEL
						</Button>
					) : residuos.isEnvioBodegaNoPeligrosos ? (
						<Button
							color='primary'
							onClick={() => residuos.openGuideDetailPicker()}
							disabled={
								!validation.values.residue_type_id ||
								!validation.values.establishment_id
							}
						>
							<i className='ri-file-list-3-line me-1'></i>
							Agregar desde guías recepcionadas
						</Button>
					) : residuos.isResiduosRegulares ? (
						<Button
							color='primary'
							onClick={() => residuos.setIsResiduosModalOpen(true)}
							disabled={!validation.values.residue_type_id}
						>
							<i className='ri-add-line me-1'></i>
							Agregar Residuo
						</Button>
					) : (
						<Button
							color='primary'
							onClick={() => {
								resetFormItem();
								setIsOpen(true);
							}}
						>
							<i className='ri-add-line me-1'></i>
							Agregar ítem
						</Button>
					)}
				</div>

				<ItemsSectionTable items={items} onRemove={handleRemove} />

				<ItemsSectionModal
					isOpen={isOpen}
					formItem={formItem}
					onSetItem={handleSetItem}
					onChange={handleChange}
					onClose={toggle}
					onAddAndContinue={() => handleAddItem(true)}
					onAddAndClose={() => handleAddItem(false)}
					onAddMultiple={handleAddMultiple}
				/>

				<RetiroEPPSelectorModal
					isOpen={epp.isRetiroModalOpen}
					onClose={() => epp.setIsRetiroModalOpen(false)}
					onConfirm={epp.handleRetiroConfirm}
				/>

				<ResiduosSelectorModal
					isOpen={residuos.isResiduosModalOpen}
					toggle={residuos.handleCloseResiduosModal}
					onAdd={residuos.handleAddResidue}
					onUpdate={residuos.handleUpdateResidue}
					residueTypeId={validation.values.residue_type_id}
					residueCategoryId={validation.values.residue_category_id || null}
					finalDestinationId={
						validation.values.destiny_type_id === '2'
							? validation.values.final_destination_id
							: null
					}
					editingDetail={residuos.editingResidueDetail}
					guideDetailsList={residuos.guideDetailsList}
					establishmentTypeId={validation.values.establishment_type || ''}
				/>

				<BeachCleaningModal
					isOpen={residuos.isBeachCleaningModalOpen}
					toggle={residuos.handleCloseBeachCleaningModal}
					onSelectCleanings={residuos.handleBeachCleaningSelected}
					currentResidueIds={residuos.currentResidueIds}
				/>

				<WarehouseResidueStockPickerModal
					isOpen={residuos.isWarehouseStockPickerOpen}
					toggle={residuos.closeWarehouseStockPicker}
					residueTypeId={validation.values.residue_type_id}
					establishmentId={validation.values.establishment_id}
					warehouseId={validation.values.warehouse_origin_id}
					existingGuideItems={validation.values.items || []}
					onConfirm={residuos.handleConfirmWarehouseStockPick}
				/>

				<WarehouseGuideDetailPickerModal
					isOpen={residuos.isGuideDetailPickerOpen}
					toggle={residuos.closeGuideDetailPicker}
					residueTypeId={validation.values.residue_type_id}
					establishmentId={validation.values.establishment_id}
					existingGuideItems={validation.values.items || []}
					onConfirm={residuos.handleConfirmGuideDetailPick}
				/>
			</CardBody>
		</Card>
	);
};

export const ItemsSection = memo(ItemsSectionComponent);
ItemsSection.displayName = 'ItemsSection';

export const ItemsSectionSpec = ItemsSectionSpecInternal;
